import React, { useEffect, useState } from "react";
import { Plus, Trash2, FileSignature, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { fmtDateGB } from "../../lib/format";
import { api, formatApiError } from "../../lib/api";
import DateInput from "../../components/DateInput";

/**
 * PPA Contract management — per-site contract records with expiry warnings.
 * Lives inside the Monitoring tab (user asked "PPA sub module setup in
 * monitoring"). Each contract carries counterparty, term, PPA rate, annual
 * escalator, notice period + status (active / expired / terminated / draft).
 *
 * Backend endpoints:
 *   GET    /projects/{pid}/ppa-contracts     — list w/ live warning objects
 *   POST   /projects/{pid}/ppa-contracts     — create
 *   PATCH  /ppa-contracts/{cid}              — update
 *   DELETE /ppa-contracts/{cid}              — remove
 */

const STATUS_STYLES = {
  active:     { bg: "bg-emerald-50",  fg: "text-emerald-800", border: "border-emerald-300" },
  draft:      { bg: "bg-zinc-50",     fg: "text-zinc-700",    border: "border-zinc-300" },
  expired:    { bg: "bg-red-50",      fg: "text-red-800",     border: "border-red-300" },
  terminated: { bg: "bg-amber-50",    fg: "text-amber-800",   border: "border-amber-300" },
};

const WARNING_STYLES = {
  ok:       { icon: CheckCircle2,   fg: "text-emerald-600", label: "On track" },
  warning:  { icon: AlertTriangle,  fg: "text-amber-600",   label: "Notice window" },
  critical: { icon: AlertTriangle,  fg: "text-red-600",     label: "Renew now" },
  expired:  { icon: XCircle,        fg: "text-red-800",     label: "Expired" },
};


export default function PPAContractsSection({ projectId }) {
  const [contracts, setContracts] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/ppa-contracts`);
      setContracts(data.contracts || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, [projectId]);

  const remove = async (cid) => {
    if (!window.confirm("Delete this PPA contract record?")) return;
    try {
      await api.delete(`/ppa-contracts/${cid}`);
      toast.success("Contract deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Aggregate expiry banner — surfaces the most urgent contract's warning at
  // the top so operators see it without scrolling the table.
  const critical = contracts.find((c) => c.warning?.level === "critical" || c.warning?.level === "expired");
  const warning = contracts.find((c) => c.warning?.level === "warning");
  const banner = critical || warning;

  return (
    <section className="swiss-card-flat p-6" data-testid="ppa-contracts-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="label flex items-center gap-2">
            <FileSignature size={11} /> PPA Contracts
          </div>
          <div className="muted text-xs mt-1">
            Manage this site&apos;s Power Purchase Agreements — terms, dates and expiry warnings.
          </div>
        </div>
        <button onClick={() => setShowModal(true)}
                className="btn-primary"
                data-testid="add-ppa-contract-btn">
          <Plus size={13} /> Add PPA contract
        </button>
      </div>

      {banner && (
        <div className={`p-4 mb-4 border-l-4 ${banner.warning.level === "critical" || banner.warning.level === "expired" ? "bg-red-50 border-red-500" : "bg-amber-50 border-amber-500"}`}
             data-testid="ppa-expiry-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className={banner.warning.level === "critical" || banner.warning.level === "expired" ? "text-red-600" : "text-amber-600"} />
            <div className="flex-1">
              <div className="font-display font-semibold text-sm">
                {banner.contract_name} — {banner.warning.message}
              </div>
              <div className="text-xs muted mt-0.5">
                Counterparty: {banner.counterparty || "—"} · Ends {fmtDateGB(banner.end_date)} · Rate {banner.ppa_rate_p_per_kwh}p/kWh
              </div>
            </div>
          </div>
        </div>
      )}

      {contracts.length === 0 ? (
        <div className="muted text-sm italic p-6 text-center border border-dashed border-[var(--line)]">
          No PPA contracts recorded for this site yet.
        </div>
      ) : (
        <div className="space-y-3">
          {contracts.map((c) => {
            const st = STATUS_STYLES[c.status] || STATUS_STYLES.draft;
            const w = WARNING_STYLES[c.warning?.level] || WARNING_STYLES.ok;
            const WIcon = w.icon;
            return (
              <div key={c.id}
                   className={`p-4 border ${st.border} ${st.bg}`}
                   data-testid={`ppa-contract-${c.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-display font-semibold text-base">{c.contract_name}</div>
                      <span className={`inline-block px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest border ${st.border} ${st.fg}`}>
                        {c.status}
                      </span>
                    </div>
                    <div className="text-xs muted">
                      {c.counterparty && <><strong>{c.counterparty}</strong> · </>}
                      <span className="font-mono">{fmtDateGB(c.start_date)}</span> → <span className="font-mono">{fmtDateGB(c.end_date)}</span>
                      {c.termination_date && <> · <span className="text-red-700">terminated {fmtDateGB(c.termination_date)}</span></>}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
                      <div>
                        <div className="label">PPA rate</div>
                        <div className="font-mono font-bold">{c.ppa_rate_p_per_kwh.toFixed(2)}p/kWh</div>
                      </div>
                      <div>
                        <div className="label">Escalator</div>
                        <div className="font-mono font-bold">{c.annual_escalator_pct || 0}%/yr</div>
                      </div>
                      <div>
                        <div className="label">Notice period</div>
                        <div className="font-mono">{c.notice_period_days || 180}d</div>
                      </div>
                      {c.minimum_offtake_kwh_yr != null && (
                        <div>
                          <div className="label">Min offtake</div>
                          <div className="font-mono">{c.minimum_offtake_kwh_yr.toLocaleString()} kWh/yr</div>
                        </div>
                      )}
                    </div>
                    {c.terms_notes && (
                      <div className="text-xs muted mt-2 italic">&ldquo;{c.terms_notes}&rdquo;</div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <div className={`flex items-center gap-1.5 ${w.fg} text-xs font-mono`}
                         data-testid={`ppa-warning-${c.id}`}>
                      <WIcon size={14} /> {c.warning?.message || w.label}
                    </div>
                    <button onClick={() => remove(c.id)}
                            className="text-red-600 hover:bg-red-100 p-1"
                            data-testid={`delete-ppa-${c.id}`}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <PPAModal projectId={projectId}
                  onClose={() => setShowModal(false)}
                  onSaved={() => { setShowModal(false); load(); }} />
      )}
    </section>
  );
}


function PPAModal({ projectId, onClose, onSaved }) {
  const [form, setForm] = useState({
    contract_name: "",
    counterparty: "",
    start_date: "",
    end_date: "",
    ppa_rate_p_per_kwh: "",
    annual_escalator_pct: 2.5,
    notice_period_days: 180,
    minimum_offtake_kwh_yr: "",
    contract_url: "",
    terms_notes: "",
    status: "active",
  });
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    if (!form.contract_name || !form.start_date || !form.end_date || !form.ppa_rate_p_per_kwh) {
      return toast.error("Contract name, start & end dates, and PPA rate are required");
    }
    setBusy(true);
    try {
      const payload = { ...form };
      // Coerce numeric fields; nullify blanks
      ["ppa_rate_p_per_kwh", "annual_escalator_pct", "notice_period_days", "minimum_offtake_kwh_yr"].forEach((k) => {
        payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k]);
      });
      // Drop nullable optionals when empty so backend defaults apply
      ["counterparty", "contract_url", "terms_notes", "minimum_offtake_kwh_yr"].forEach((k) => {
        if (payload[k] === "" || payload[k] == null) delete payload[k];
      });
      await api.post(`/projects/${projectId}/ppa-contracts`, payload);
      toast.success("PPA contract saved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/45 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="ppa-modal">
      <div className="bg-white w-full max-w-2xl border border-[var(--line)]"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-[var(--line)]">
          <div className="label">Add PPA Contract</div>
          <div className="font-display text-xl font-semibold mt-1">New agreement</div>
        </div>
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <div className="label mb-1.5">Contract name *</div>
            <input className="input-swiss w-full" value={form.contract_name}
                   onChange={upd("contract_name")}
                   placeholder="Viables Business Park PPA 2024"
                   data-testid="ppa-form-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="label mb-1.5">Counterparty</div>
              <input className="input-swiss w-full" value={form.counterparty}
                     onChange={upd("counterparty")}
                     placeholder="LG Energy Solutions"
                     data-testid="ppa-form-counterparty" />
            </div>
            <div>
              <div className="label mb-1.5">Status</div>
              <select className="input-swiss w-full" value={form.status}
                      onChange={upd("status")} data-testid="ppa-form-status">
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="expired">Expired</option>
                <option value="terminated">Terminated</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="label mb-1.5">Start date *</div>
              <DateInput className="input-swiss font-mono w-full"
                     value={form.start_date} onChange={upd("start_date")}
                     data-testid="ppa-form-start" />
            </div>
            <div>
              <div className="label mb-1.5">End date *</div>
              <DateInput className="input-swiss font-mono w-full"
                     value={form.end_date} onChange={upd("end_date")}
                     data-testid="ppa-form-end" />
            </div>
            <div>
              <div className="label mb-1.5">Termination date</div>
              <DateInput className="input-swiss font-mono w-full"
                     value={form.termination_date || ""} onChange={upd("termination_date")}
                     data-testid="ppa-form-termination" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="label mb-1.5">PPA rate (p/kWh) *</div>
              <input type="number" step="0.01" className="input-swiss font-mono w-full"
                     value={form.ppa_rate_p_per_kwh} onChange={upd("ppa_rate_p_per_kwh")}
                     placeholder="18.00" data-testid="ppa-form-rate" />
            </div>
            <div>
              <div className="label mb-1.5">Escalator (%/yr)</div>
              <input type="number" step="0.1" className="input-swiss font-mono w-full"
                     value={form.annual_escalator_pct} onChange={upd("annual_escalator_pct")}
                     data-testid="ppa-form-escalator" />
            </div>
            <div>
              <div className="label mb-1.5">Notice period (days)</div>
              <input type="number" step="1" className="input-swiss font-mono w-full"
                     value={form.notice_period_days} onChange={upd("notice_period_days")}
                     data-testid="ppa-form-notice" />
            </div>
          </div>
          <div>
            <div className="label mb-1.5">Minimum offtake (kWh/yr)</div>
            <input type="number" className="input-swiss font-mono w-full"
                   value={form.minimum_offtake_kwh_yr} onChange={upd("minimum_offtake_kwh_yr")}
                   placeholder="180000" data-testid="ppa-form-offtake" />
          </div>
          <div>
            <div className="label mb-1.5">Contract URL</div>
            <input className="input-swiss w-full" value={form.contract_url}
                   onChange={upd("contract_url")}
                   placeholder="https://…"
                   data-testid="ppa-form-url" />
          </div>
          <div>
            <div className="label mb-1.5">Terms & notes</div>
            <textarea className="input-swiss w-full min-h-[70px]" value={form.terms_notes}
                      onChange={upd("terms_notes")}
                      placeholder="15-year PPA with RPI-linked annual uplift…"
                      data-testid="ppa-form-notes" />
          </div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-[var(--line)] bg-[var(--surface-2)]">
          <button onClick={onClose} className="btn-ghost" data-testid="ppa-form-cancel">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-accent" data-testid="ppa-form-save">
            {busy ? "Saving…" : "Save contract"}
          </button>
        </div>
      </div>
    </div>
  );
}
